import React, { useMemo, useState, useRef } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { VENDORS } from '../data/erpData';
import { useLang } from '../i18n/LangContext';
import GridToolbar from '../components/GridToolbar';
import '../styles/DataGrid.css';

const VendorsPage = () => {
  const gridRef = useRef();
  const { t, fmtCurrency, lang } = useLang();
  const [rowData, setRowData] = useState(VENDORS.map((v, i) => ({ ...v, _id: i })));

  const groupLabel = (g) => {
    if (lang === 'ja') {
      if (g === '메모리') return 'メモリ';
      if (g === '비메모리') return '非メモリ';
      if (g === 'SiP 외') return 'SiP他';
    }
    return g;
  };

  const columnDefs = useMemo(() => [
    { headerCheckboxSelection: true, checkboxSelection: true, width: 48, pinned: 'left', lockPosition: true, suppressMenu: true, resizable: false },
    { field: 'code', headerName: t.vendors.code, width: 120, pinned: 'left', editable: true },
    { field: 'name', headerName: t.vendors.name, width: 210, editable: true },
    { field: 'group', headerName: t.inventory.group, width: 100,
      valueFormatter: params => groupLabel(params.value),
      cellStyle: params => {
        const colors = { '메모리': '#dbeafe', '비메모리': '#f3e8ff', 'SiP 외': '#ccfbf1' };
        return { backgroundColor: colors[params.value] || 'transparent', fontWeight: 500 };
      }
    },
    { field: 'vCode', headerName: lang === 'ko' ? '공급자코드' : 'サプライヤーコード', width: 120, editable: true },
    { field: 'vName', headerName: t.vendors.vendorName, width: 130, editable: true,
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: { values: ['㈜성동전자', '㈜CS전자', 'Arrow Elec.', '㈜민수산업'] },
      cellStyle: params => {
        const colors = { '㈜성동전자': '#fef3c7', '㈜CS전자': '#dbeafe', 'Arrow Elec.': '#fce7f3', '㈜민수산업': '#dcfce7' };
        return { backgroundColor: colors[params.value] || 'transparent', fontWeight: 500 };
      }
    },
    { field: 'moq', headerName: 'MOQ', width: 90, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0 },
    { field: 'lot', headerName: 'LOT', width: 90, editable: true, type: 'numericColumn',
      valueFormatter: params => params.value?.toLocaleString(), valueParser: params => Number(params.newValue) || 0 },
    { field: 'cost', headerName: t.vendors.cost, width: 120, editable: true, type: 'numericColumn',
      valueFormatter: params => fmtCurrency(params.value), valueParser: params => Number(params.newValue) || 0 },
    { field: 'lt', headerName: t.vendors.lt, width: 110, editable: true, type: 'numericColumn',
      valueFormatter: params => `${params.value}${t.dashboard.day}`, valueParser: params => Number(params.newValue) || 0,
      cellStyle: params => params.value >= 10 ? { color: '#dc2626', fontWeight: 600 } : {} },
    { field: 'defRate', headerName: t.vendors.defRate, width: 90, editable: true, type: 'numericColumn',
      valueFormatter: params => `${(params.value * 100).toFixed(1)}%`,
      valueParser: params => { const v = parseFloat(params.newValue); return v > 1 ? v / 100 : v; },
      cellStyle: params => ({ color: params.value >= 0.025 ? '#dc2626' : params.value >= 0.01 ? '#f59e0b' : '#16a34a', fontWeight: 600 }) },
  ], [t, lang, fmtCurrency]);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true }), []);

  const newRowTemplate = (data) => ({
    _id: Date.now(), code: '', name: '', group: '', cls: '',
    vCode: `V${String(data.length + 1).padStart(4, '0')}`, vName: '㈜성동전자',
    moq: 100, lot: 50, cost: 0, lt: 0, defRate: 0,
  });

  const vendorStats = useMemo(() => {
    const map = {};
    rowData.forEach(r => {
      if (!map[r.vName]) map[r.vName] = { count: 0, totalLt: 0 };
      map[r.vName].count++; map[r.vName].totalLt += r.lt;
    });
    Object.keys(map).forEach(k => { map[k].avgLt = (map[k].totalLt / map[k].count).toFixed(1); });
    return map;
  }, [rowData]);

  return (
    <div className="data-page">
      <div className="data-page-header">
        <div className="data-page-title-area">
          <h2 className="data-page-title">{t.vendors.title}</h2>
          <span className="data-page-count">{rowData.length}{lang === 'ko' ? '건 거래정보' : '件の取引情報'}</span>
        </div>
        <div className="data-page-badges">
          {Object.entries(vendorStats).map(([name, stat]) => (
            <span key={name} className="badge badge-gray">{name}: {stat.count}{t.dashboard.count}</span>
          ))}
        </div>
      </div>
      <GridToolbar gridRef={gridRef} rowData={rowData} setRowData={setRowData} newRowTemplate={newRowTemplate} entityName={lang === 'ko' ? '거래' : '取引'} />
      <div className="ag-theme-alpine data-grid-wrapper">
        <AgGridReact ref={gridRef} rowData={rowData} columnDefs={columnDefs} defaultColDef={defaultColDef}
          animateRows={true} rowSelection="multiple" enableCellTextSelection={true} undoRedoCellEditing={true}
          stopEditingWhenCellsLoseFocus={true} suppressRowClickSelection={true}
        />
      </div>
    </div>
  );
};

export default VendorsPage;
